/**
 * Admin: matriz menú lateral × perfil de negocio.
 * Layout por cards (sin scroll horizontal de 14+ columnas).
 */
import { useEffect, useMemo, useState } from "react";
import { ChevronLeft } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { apiGetJson, apiSendJson } from "@/lib/sm-api";
import {
  PROFILE_NAV_FEATURES,
  PROFILE_NAV_GROUP_LABELS,
  PROFILE_NAV_PROFILES,
  defaultProfileNavEnabled,
  profileNavLabel,
  type ProfileNavFeatureDef,
} from "@/lib/profile-nav-matrix";

interface NavRow {
  businessProfile: string;
  navPath: string;
  enabled: boolean;
}

const GROUP_ORDER: ProfileNavFeatureDef["group"][] = [
  "catalogo",
  "comercio",
  "ops",
  "experiencia",
];

export function ProfileNavMatrixPanel() {
  const [matrix, setMatrix] = useState<NavRow[]>([]);
  const [saving, setSaving] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [loaded, setLoaded] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState<string | null>(null);

  const byPath = useMemo(() => {
    const m = new Map<string, boolean>();
    for (const r of matrix) m.set(`${r.businessProfile}:${r.navPath}`, r.enabled);
    return m;
  }, [matrix]);

  useEffect(() => {
    apiGetJson<NavRow[] | { error?: string }>("/api/admin/profile-nav")
      .then((data) => {
        if (!Array.isArray(data)) throw new Error("Respuesta inesperada del servidor");
        setMatrix(data);
        setLoaded(true);
      })
      .catch((err) => {
        console.error("[ProfileNavMatrixPanel]", err);
        toast.error("Error al cargar configuración");
        setLoaded(true);
      });
  }, []);

  const getValue = (profile: string, path: string): boolean => {
    const key = `${profile}:${path}`;
    if (byPath.has(key)) return byPath.get(key)!;
    return defaultProfileNavEnabled(profile, path);
  };

  const toggle = async (profile: string, path: string, enabled: boolean) => {
    const key = `${profile}:${path}`;
    setSaving(key);
    try {
      await apiSendJson("/api/admin/profile-nav", "POST", {
        businessProfile: profile,
        navPath: path,
        enabled,
      });
      setMatrix((prev) => {
        const next = prev.filter((x) => !(x.businessProfile === profile && x.navPath === path));
        return [...next, { businessProfile: profile, navPath: path, enabled }];
      });
    } catch {
      toast.error("No se pudo guardar");
    } finally {
      setSaving(null);
    }
  };

  const featuresByGroup = useMemo(() => {
    return GROUP_ORDER.map((g) => ({
      group: g,
      label: PROFILE_NAV_GROUP_LABELS[g],
      features: PROFILE_NAV_FEATURES.filter((f) => f.group === g),
    })).filter((g) => g.features.length > 0);
  }, []);

  const profiles = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return PROFILE_NAV_PROFILES;
    return PROFILE_NAV_PROFILES.filter((p) => profileNavLabel(p).toLowerCase().includes(q));
  }, [query]);

  if (loaded && selectedProfile) {
    const profile = selectedProfile;
    const onCount = PROFILE_NAV_FEATURES.filter((f) => getValue(profile, f.path)).length;
    return (
      <div className="space-y-4">
        <button
          type="button"
          onClick={() => setSelectedProfile(null)}
          className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
        >
          <ChevronLeft className="h-3.5 w-3.5" /> Todos los perfiles
        </button>

        <div className="flex items-baseline justify-between gap-2">
          <h3 className="text-base font-semibold leading-tight">{profileNavLabel(profile)}</h3>
          <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
            {onCount}/{PROFILE_NAV_FEATURES.length} on · <span className="font-mono">{profile}</span>
          </span>
        </div>

        <p className="text-[11px] text-muted-foreground">
          Siempre visibles: Inicio, Órdenes y Configuración. Sin fila en DB se usa el default del
          rubro (<code className="text-[10px]">onlyProfiles</code>). Cambios instantáneos (caché del
          sidebar ~2 min).
        </p>

        <div className="space-y-4">
          {featuresByGroup.map((g) => (
            <div key={g.group}>
              <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                {g.label}
              </p>
              <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
                {g.features.map((feat) => {
                  const key = `${profile}:${feat.path}`;
                  const enabled = getValue(profile, feat.path);
                  const isDefault = !byPath.has(key);
                  return (
                    <label
                      key={feat.path}
                      className={`flex min-h-[40px] cursor-pointer items-center justify-between gap-2 rounded-lg border px-2.5 py-1.5 text-xs transition-colors ${
                        enabled ? "border-primary/35 bg-primary/10" : "border-border/70 bg-background/40"
                      }`}
                    >
                      <span className="min-w-0 leading-snug">
                        <span className="font-medium">{feat.label}</span>
                        {isDefault ? (
                          <span className="mt-0.5 block text-[9px] text-muted-foreground">default rubro</span>
                        ) : null}
                      </span>
                      <Switch
                        checked={enabled}
                        disabled={saving === key}
                        onCheckedChange={(v) => void toggle(profile, feat.path, v)}
                      />
                    </label>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div className="max-w-2xl space-y-1">
          <p className="text-sm text-muted-foreground">
            Elegí un perfil de negocio para habilitar o deshabilitar módulos del menú lateral. Los
            cambios se guardan al instante (caché del sidebar ~2 min).
          </p>
        </div>
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Filtrar perfil…"
          className="h-9 w-full rounded-lg border border-border bg-background px-3 text-sm sm:w-56"
        />
      </div>

      {!loaded ? (
        <p className="text-sm text-muted-foreground">Cargando matriz…</p>
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {profiles.map((profile) => {
            const onCount = PROFILE_NAV_FEATURES.filter((f) => getValue(profile, f.path)).length;
            return (
              <button
                key={profile}
                type="button"
                onClick={() => setSelectedProfile(profile)}
                className="rounded-xl border border-border bg-card/40 p-4 text-left shadow-sm transition-colors hover:border-primary/40 hover:bg-card/70"
              >
                <h3 className="text-sm font-semibold leading-tight">{profileNavLabel(profile)}</h3>
                <p className="mt-1 text-[11px] tabular-nums text-muted-foreground">
                  {onCount}/{PROFILE_NAV_FEATURES.length} módulos on · <span className="font-mono">{profile}</span>
                </p>
              </button>
            );
          })}
        </div>
      )}

      {loaded && profiles.length === 0 ? (
        <p className="text-sm text-muted-foreground">Ningún perfil coincide con el filtro.</p>
      ) : null}
    </div>
  );
}
